import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';

import { ACTIVE_TASKS_SORT_ORDER, TASKS_CONFIGS } from '../../../constants';
import type { Task, TaskShape } from '../../../types';

import { useCompleteTaskByCheckbox } from './useCompleteTaskByCheckbox';
import { useDescription } from './useDescription';
import { useGetAction } from './useGetAction';
import { useGetHref } from './useGetHref';
import { useTitle } from './useTitle';

type UseTasksReturn = {
	tasks: TaskShape[];
	stats: {
		completed: number;
		count: number;
	};
};

/**
 * Represents the shape of the tasks data returned from the backend.
 * It can either be a valid connection with nodes and totalCount, or an unauthorized error.
 * This type helps in safely handling the union type returned by the GraphQL query.
 */
type TasksItem =
	| {
			__typename: 'PetitionTasksConnection';
			nodes: readonly Task[];
			totalCount: number;
	  }
	| {
			__typename: 'PetitionTasksConnectionUnauthorizedError';
	  };

/**
 * Validates if the given value matches the structure of a TasksItem.
 * This ensures we are dealing with the expected response format from the server
 * (either a list of tasks or an error) before trying to access its properties.
 */
function isTasksItem(value: unknown): value is TasksItem {
	return typeof value === 'object' && value !== null && '__typename' in value;
}

function sortActiveTasks(activeTasks: readonly Task[]): Task[] {
	return [...activeTasks].sort((a, b) => {
		const indexA = ACTIVE_TASKS_SORT_ORDER.indexOf(a.name);
		const indexB = ACTIVE_TASKS_SORT_ORDER.indexOf(b.name);

		// If both tasks are in the sort order, sort by their index
		if (indexA !== -1 && indexB !== -1) {
			return indexA - indexB;
		}
		// If only one is in the sort order, prioritize it
		if (indexA !== -1) return -1;
		if (indexB !== -1) return 1;
		// Keep original order for unlisted tasks
		return 0;
	});
}

function getUpdatedAtTime(task: Task): number {
	// oxlint-disable-next-line prefer-destructuring
	const updatedAt: unknown = task.updatedAt;
	if (typeof updatedAt === 'string') {
		return new Date(updatedAt).getTime();
	}
	return 0;
}

function sortCompletedTasks(completedTasks: readonly Task[]): Task[] {
	return [...completedTasks].sort((a, b) => {
		const dateA = getUpdatedAtTime(a);
		const dateB = getUpdatedAtTime(b);
		return dateA - dateB; // ascending (oldest first, most recent at bottom)
	});
}

function getCombinedTasks(
	activeTasksUnknown: unknown,
	completedTasksUnknown: unknown,
): { tasks: Task[]; stats: { count: number; completed: number } } {
	if (
		!isTasksItem(activeTasksUnknown) ||
		!isTasksItem(completedTasksUnknown) ||
		activeTasksUnknown.__typename !== 'PetitionTasksConnection' ||
		completedTasksUnknown.__typename !== 'PetitionTasksConnection'
	) {
		return { tasks: [], stats: { count: 0, completed: 0 } };
	}

	// Sort active tasks according to ACTIVE_TASKS_SORT_ORDER
	const sortedActiveTasks = sortActiveTasks(activeTasksUnknown.nodes);

	// Sort completed tasks by updatedAt ascending (oldest first, most recent at bottom)
	const sortedCompletedTasks = sortCompletedTasks(completedTasksUnknown.nodes);

	const tasks = [...sortedActiveTasks, ...sortedCompletedTasks];
	return {
		tasks,
		stats: {
			count: activeTasksUnknown.totalCount + completedTasksUnknown.totalCount,
			completed: completedTasksUnknown.totalCount,
		},
	};
}

export function useTasks(): UseTasksReturn {
	const { typedTranslate } = useI18n();
	const { data } = useStarterDashboardV2PageData();
	const getHref = useGetHref();
	const getAction = useGetAction();
	const getTitle = useTitle();
	const getDescription = useDescription();
	const getCheckboxHandler = useCompleteTaskByCheckbox();

	// combine active + completed tasks
	const combinedTasks = useMemo(
		() => getCombinedTasks(data.petition.activeTasks, data.petition.allCompletedTasks),
		[data.petition.activeTasks, data.petition.allCompletedTasks],
	);

	const canEditUrl = !data.petition.starterVanityUrl;

	const tasks = combinedTasks.tasks
		.filter(
			(task): task is Task & { name: keyof typeof TASKS_CONFIGS } =>
				Object.hasOwn(TASKS_CONFIGS, task.name) && task.name !== 'share_social',
		)
		.map((task) => {
			const key = task.name;
			// oxlint-disable-next-line prefer-destructuring
			const metadata: unknown = task.metadata;

			return {
				...task,
				primaryActionText: typedTranslate(TASKS_CONFIGS[key].primaryActionText),
				// dynamic properties
				title: getTitle({ name: task.name, metadata }),
				description: getDescription({ name: task.name, metadata }),
				primaryActionHref: getHref({ name: task.name }),
				primaryActionOnClick: getAction({ id: task.id, name: task.name, status: task.status }),
				onCheckboxChange: getCheckboxHandler(task.id, task.name, task.status),
				showActionButton: task.name === 'customize_link' ? canEditUrl : true,
			};
		});

	const stats = {
		completed: tasks.filter((task) => task.status === 'COMPLETED').length,
		count: tasks.length,
	};

	return {
		tasks,
		stats,
	};
}
